import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "../lib/api";
import type { Company, Estate, Region } from "../lib/types";

interface EstateFormProps {
  initial?: Estate;
  defaultCompanyId?: string;
  onSaved: (estate: Estate) => void;
  onCancel: () => void;
}

export function EstateForm({ initial, defaultCompanyId, onSaved, onCancel }: EstateFormProps) {
  const isEdit = Boolean(initial);
  const qc = useQueryClient();

  const [companyId, setCompanyId] = useState(initial?.companyId ?? defaultCompanyId ?? "");
  const [code, setCode] = useState(initial?.code ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [type, setType] = useState<"INTI" | "PLASMA" | null>(initial?.type ?? null);
  const [titleAreaHa, setTitleAreaHa] = useState(initial?.titleAreaHa ?? "");
  const [titleNumber, setTitleNumber] = useState(initial?.titleNumber ?? "");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const companiesQ = useQuery({
    queryKey: ["companies"],
    queryFn: () => api.list<Company>("/v1/companies"),
  });
  const regionsQ = useQuery({
    queryKey: ["regions"],
    queryFn: () => api.list<Region>("/v1/regions"),
  });

  /**
   * Resolve the country of the selected company's region so the Type
   * (Inti/Plasma) field is shown ONLY for Indonesian estates. Malaysian
   * companies leave `type` blank.
   */
  const country = useMemo(() => {
    const c = (companiesQ.data?.data ?? []).find((x) => x.id === companyId);
    const r = (regionsQ.data?.data ?? []).find((x) => x.id === c?.regionId);
    return r?.country ?? "MY";
  }, [companiesQ.data, regionsQ.data, companyId]);

  const isIndonesia = country === "ID";

  const submitMut = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        code,
        name,
        // Only send type when Indonesia — Malaysian estates save null.
        type: isIndonesia ? type : null,
      };
      if (titleAreaHa) payload.titleAreaHa = titleAreaHa;
      if (titleNumber) payload.titleNumber = titleNumber;
      if (!isEdit) payload.companyId = companyId;
      if (isIndonesia && !type) {
        setFieldErrors({ type: ["Required for Indonesian estates"] });
        throw new Error("Estate type required for Indonesian estates");
      }
      if (isEdit) {
        const res = await api.patch<Estate>(`/v1/estates/${initial!.id}`, payload);
        return res.data;
      }
      const res = await api.post<Estate>("/v1/estates", payload);
      return res.data;
    },
    onSuccess: (estate) => {
      qc.invalidateQueries({ queryKey: ["estates"] });
      onSaved(estate);
    },
    onError: (err) => {
      setFormError(null);
      if (err instanceof ApiError) {
        const d = err.details as { fieldErrors?: Record<string, string[]> } | undefined;
        if (d?.fieldErrors) setFieldErrors(d.fieldErrors);
        else setFormError(err.message);
      } else if (!fieldErrors.type) {
        setFormError((err as Error).message ?? "Unknown error");
      }
    },
  });

  const f = (name: string) => fieldErrors[name]?.[0];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setFieldErrors({});
        submitMut.mutate();
      }}
      className="space-y-4"
    >
      {formError && (
        <div className="bg-bg-danger text-text-danger border border-border-danger rounded p-2 text-xs">
          {formError}
        </div>
      )}

      <Row label="Company" error={f("companyId")}>
        <select
          className="input"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          disabled={isEdit || companiesQ.isLoading}
        >
          <option value="">— select —</option>
          {(companiesQ.data?.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} · {c.name}
            </option>
          ))}
        </select>
      </Row>

      <div className={`grid gap-3 ${isIndonesia ? "grid-cols-2" : "grid-cols-1"}`}>
        <Row label="Estate code" error={f("code")} hint="e.g. 21">
          <input className="input font-mono" value={code} onChange={(e) => setCode(e.target.value)} maxLength={4} />
        </Row>
        {isIndonesia && (
          <Row
            label="Type (Indonesia only)"
            error={f("type")}
            hint="INTI = own · PLASMA = smallholder scheme"
          >
            <div className="flex gap-2">
              {(["INTI", "PLASMA"] as const).map((t) => (
                <label
                  key={t}
                  className={`px-3 py-1.5 rounded-full border text-xs cursor-pointer transition-colors ${
                    type === t
                      ? "bg-bg-accent text-text-accent border-border-accent font-medium"
                      : "bg-surface-1 border-border-strong text-text-secondary"
                  }`}
                >
                  <input type="radio" name="type" value={t} checked={type === t} onChange={() => setType(t)} className="sr-only" />
                  {t}
                </label>
              ))}
            </div>
          </Row>
        )}
      </div>

      {!isIndonesia && (
        <div className="text-[10.5px] text-text-secondary">
          Estate type (INTI/PLASMA) is not applicable for {country} — leaving blank.
        </div>
      )}

      <Row label="Estate name" error={f("name")}>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Row>

      <div className="grid grid-cols-2 gap-3 border-t border-border pt-3">
        <Row label="Title area (ha)" error={f("titleAreaHa")} hint="Legacy field — prefer 'Manage titles' for M:N">
          <input
            className="input font-mono"
            inputMode="decimal"
            placeholder="500.00"
            value={titleAreaHa}
            onChange={(e) => setTitleAreaHa(e.target.value)}
          />
        </Row>
        <Row label="Title number" error={f("titleNumber")} hint="Legacy · use Titles button on row">
          <input className="input" value={titleNumber} onChange={(e) => setTitleNumber(e.target.value)} />
        </Row>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={submitMut.isPending}>
          {submitMut.isPending ? "Saving…" : isEdit ? "Save changes" : "Create estate"}
        </button>
      </div>
    </form>
  );
}

function Row({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      {children}
      {hint && !error && <div className="text-[10.5px] text-text-muted mt-1">{hint}</div>}
      {error && <div className="text-[10.5px] text-text-danger mt-1">{error}</div>}
    </div>
  );
}
